import { ApplicationRef, Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { MatDialog } from '@angular/material/dialog';
import { filter, first } from 'rxjs/operators';
import { environment } from '@sp-environments/environment';
import { concat, fromEvent, interval, Subscription } from 'rxjs';
import { AppUpdateDialogComponent } from '@sp-shared/components/app-update-dialog/app-update-dialog.component';
import { NavigationEnd, Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  private updateModalShown = false;
  private autoReloadTimer: any = null;
  private subs = new Subscription();
  private updateAvailable = false;

  private readonly enableDetailedLogs = environment.serviceWorker?.enableDetailedLogs ?? false;

  constructor(
    private swUpdate: SwUpdate,
    private appRef: ApplicationRef,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  private log(message: string, ...args: any[]): void {
    if (this.enableDetailedLogs) {
      console.log(message, ...args);
    }
  }

  private logError(message: string, ...args: any[]): void {
    // Always log errors, even in production
    console.error(message, ...args);
  }

  private logWarning(message: string, ...args: any[]): void {
    if (this.enableDetailedLogs) {
      console.warn(message, ...args);
    }
  }

  private logImportant(message: string, ...args: any[]): void {
    // Always log important events (like "new version available")
    console.log(message, ...args);
  }

  public init(): void {
    if (!this.swUpdate.isEnabled) {
      this.log('[AppUpdate] Service Worker is not enabled');
      return;
    }

    this.logImportant('[AppUpdate] Initializing Service Worker update checks');

    this.scheduleUpdateChecks();
    this.listenForUpdates();
  }

  private scheduleUpdateChecks() {
    const appIsStable$ = this.appRef.isStable.pipe(
      filter((isStable) => isStable === true),
      first(),
    );

    const checkInterval = environment.serviceWorker?.checkInterval ?? 5 * 60 * 1000;
    const periodicChecks$ = interval(checkInterval);
    this.log(`[AppUpdate] Check interval set to ${checkInterval / 1000} seconds at ${new Date().toLocaleTimeString()}`);

    let checkCount = 0;
    const periodicSub = concat(appIsStable$, periodicChecks$).subscribe(async () => {
      checkCount++;
      this.log(`[AppUpdate] Check #${checkCount} triggered at ${new Date().toLocaleTimeString()}`);

      if (document.hidden) {
        this.log(`[AppUpdate] Check #${checkCount} SKIPPED - tab is hidden`);
        return;
      }

      if (this.updateModalShown) {
        this.log(`[AppUpdate] Check #${checkCount} SKIPPED - update has already been detected`);
        return;
      }

      this.log(`[AppUpdate] Check #${checkCount} - Checking for updates... (${new Date().toLocaleTimeString()})`);

      try {
        const updateFound = await this.swUpdate.checkForUpdate();
        if (updateFound) {
          this.logImportant(`[AppUpdate] Check #${checkCount} - Update check found a new version.`);
        } else {
          this.log(`[AppUpdate] Check #${checkCount} - No updates available.`);
        }
      } catch (err) {
        this.logError(`[AppUpdate] Check #${checkCount} - Failed to check for updates:`, err);
      }
    });
    this.subs.add(periodicSub);

    const visSub = fromEvent(document, 'visibilitychange').subscribe(() => {
      if (!document.hidden && !this.updateModalShown) {
        this.log('[AppUpdate] Tab became visible, checking for updates');
        this.swUpdate
          .checkForUpdate()
          .catch((err) => this.logError('[AppUpdate] Check failed on visibility change:', err));
      }
    });
    this.subs.add(visSub);
  }

  private listenForUpdates(): void {
    const versionSub = this.swUpdate.versionUpdates.subscribe((evt) => {
      this.log('[AppUpdate] Version update event:', evt);

      switch (evt.type) {
        case 'VERSION_DETECTED':
          this.logImportant(`[AppUpdate] Downloading new app version: ${evt.version.hash}`);
          break;

        case 'VERSION_READY':
          this.logImportant('[AppUpdate] VERSION_READY - New version available!!!');
          this.logImportant(`[AppUpdate] Current: ${evt.currentVersion.hash}`);
          this.logImportant(`[AppUpdate] Latest: ${evt.latestVersion.hash}`);
          this.updateAvailable = true;
          this.attemptToShowUpdateDialog();
          break;

        case 'VERSION_INSTALLATION_FAILED':
          this.logError('[AppUpdate] Failed to install app version:', evt);
          this.logError('[AppUpdate] Error details:', JSON.stringify(evt, null, 2));
          break;

        case 'NO_NEW_VERSION_DETECTED':
          this.log('[AppUpdate] No new version detected');
          break;
      }
    });
    this.subs.add(versionSub);

    // Handle unrecoverable state
    const unrecoverableSub = this.swUpdate.unrecoverable.subscribe((event) => {
      this.logError('[AppUpdate] Unrecoverable state:', event.reason);
      alert('An error occurred that we cannot recover from:\n' + event.reason + '\nPlease reload the page.');
      window.location.reload();
    });
    this.subs.add(unrecoverableSub);

    // Listen for navigation to retry showing dialog if needed
    const navSub = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      if (this.updateAvailable && !this.updateModalShown) {
        this.log('[AppUpdate] Navigation detected with update available, attempting to show dialog');
        this.attemptToShowUpdateDialog();
      }
    });
    this.subs.add(navSub);
  }

  private attemptToShowUpdateDialog(): void {
    if (this.updateModalShown) {
      return;
    }

    if (!this.updateAvailable) {
      return;
    }

    if (!this.canShowUpdateDialog()) {
      this.log('[AppUpdate] Cannot show dialog now, will retry on next navigation or dialog close');
      return;
    }

    this.showUpdateDialog();
  }

  private showUpdateDialog(): void {
    if (this.updateModalShown) {
      return;
    }

    this.updateModalShown = true;

    const autoReloadDelayMs = environment.serviceWorker?.autoReloadDelay ?? 15 * 60 * 1000;
    const autoReloadDelayMinutes = Math.round(autoReloadDelayMs / 60000);

    this.logImportant('[AppUpdate] Showing update dialog');

    const dialogRef = this.dialog.open(AppUpdateDialogComponent, {
      width: '530px',
      disableClose: true,
      panelClass: 'app-update-dialog',
      hasBackdrop: true,
      data: {
        autoReloadDelayMinutes,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'update-now') {
        this.logImportant('[AppUpdate] User chose to update now');
        this.cancelAutoReload();
        this.activateUpdate();
      } else {
        this.logImportant(`[AppUpdate] User chose to update later - auto-reload in ${autoReloadDelayMinutes} minutes`);
        this.scheduleAutoReload(autoReloadDelayMs);
      }
    });
  }

  /**
   * Activates the update by reloading the page.
   * The Service Worker will automatically serve the new version after VERSION_READY.
   */
  private activateUpdate(): void {
    this.logImportant('[AppUpdate] Reloading to activate update...');
    window.location.reload();
  }

  private cancelAutoReload(): void {
    if (this.autoReloadTimer) {
      clearTimeout(this.autoReloadTimer);
      this.autoReloadTimer = null;
      this.log('[AppUpdate] Auto-reload timer cancelled');
    }
  }

  private scheduleAutoReload(delayMs: number): void {
    this.log(`[AppUpdate] Scheduling auto-reload in ${delayMs / 1000} seconds`);

    this.autoReloadTimer = setTimeout(() => {
      this.logImportant('[AppUpdate] Auto-reload timer expired, activating update...');
      this.activateUpdate();
    }, delayMs);
  }

  private canShowUpdateDialog(): boolean {
    const openDialogsCount = this.dialog.openDialogs.length;

    if (openDialogsCount > 0) {
      this.log('[AppUpdate] Delaying update notification - user has dialogs open');

      if (!this.updateModalShown) {
        const lastDialog = this.dialog.openDialogs[openDialogsCount - 1];
        const dialogSub = lastDialog.afterClosed().subscribe(() => {
          setTimeout(() => {
            if (!this.updateModalShown && this.updateAvailable) {
              this.attemptToShowUpdateDialog();
            }
          }, 2000);
        });
        this.subs.add(dialogSub);
      }
      return false;
    }
    return true;
  }

  // Is used for manual update check
  public async checkForUpdateNow(): Promise<boolean> {
    this.log('[AppUpdate] Manual update check triggered');
    return this.swUpdate.checkForUpdate();
  }

  public destroy(): void {
    this.cancelAutoReload();
    this.subs.unsubscribe();
    this.log('[AppUpdate] Cleaned up subscriptions and timers');
  }
}
